/**
 * Everything that has happened to one person, in the order it happened.
 *
 * Their story is told in four places and joined nowhere: the tracker
 * writes the arrival on the pipeline, the activities table holds the calls
 * and the stage moves, the demo session holds the booking, the hand-over
 * and every round of chasing, and the client record holds the day they
 * started paying. Each screen shows its own quarter, so "what happened
 * with this one" means opening four things and sorting by hand.
 *
 * ## Why it starts from either end
 *
 * A demo is booked on Calendly and lands on the bookings side, and most
 * of them never were a pipeline lead: of 337 sessions on this database,
 * 245 have no lead behind them at all — they booked straight off the
 * website. A journey that could only be built from a deal would have
 * nothing to say about two thirds of the people who sat through a demo.
 *
 * So it builds from whichever end is in hand and reaches for the other:
 * given a deal it looks for the booking, given a booking it looks for the
 * deal, and it draws whatever it finds.
 *
 * ## What is not in it
 *
 * Tasks nobody has done. An activity with `done: false` is a plan, not a
 * thing that happened, and there are over a hundred of them here left
 * from a sync that used to write one per lead. They come back under `due`
 * instead, so a panel can show them as what is owed rather than as what
 * took place.
 *
 * Nothing here writes. Every event already exists; the work is finding it
 * and putting it in order.
 */
import { parseDate } from '../lib/dates.js';
import { dealActivities, clientOfDeal } from './crm.js';

/*
 * How an event reads. The icon and tone say what kind of thing it was
 * before the words are read: the lead arriving, somebody doing the work,
 * the board moving, the demo team handing over, money starting.
 */
const KIND = {
  arrived: ['zap', 'g-pink', 'Arrived'],
  booked: ['cal', 'g-blue', 'Booked'],
  work: ['phone', 'g-cyan', 'Work'],
  took: ['users', 'g-amber', 'Taken on'],
  stage: ['refresh', 'g-purple', 'Stage'],
  demo: ['users', 'g-blue', 'Demo'],
  handover: ['send', 'g-amber', 'Hand-over'],
  round: ['flag', 'g-indigo', 'Follow-up'],
  lost: ['x', 'g-slate', 'Lost'],
  /*
   * Promoted, which is not winning.
   *
   * Winning is money agreed. This is somebody deciding a lead is real enough
   * to work as a deal, which happens weeks earlier — and it read as 'Won' on
   * every journey, so a board of leads nobody had closed looked like a month
   * of wins.
   */
  converted: ['refresh', 'g-indigo', 'Converted'],
  /* They asked us to stop. The end of the trail, and the only line on it
     that is worth reading six weeks later. */
  dropped: ['x', 'g-red', 'Not interested'],
  client: ['users', 'g-teal', 'Client']
};

export const kindMeta = k => KIND[k] || KIND.work;

const day = d => d.toISOString().slice(0, 10);
const norm = v => String(v || '').trim().toLowerCase();

/*
 * Kinds where a repeat is somebody doing the thing twice, and therefore
 * worth showing. Everywhere else a repeat is two subsystems recording one
 * event — winning a deal writes a stage note here, an activity on the
 * server and a state on the row, and "Won ×3" would be a lie about a
 * single win.
 */
const COUNTABLE = new Set(['work', 'stage']);

/**
 * Fold a thing that was recorded twice into one line.
 *
 * Live data has "Moved to Proposal" three times on the same day against
 * one deal — somebody clicking a stage, or two syncs agreeing. Three
 * identical rows on a spine is not three events, it is one event and two
 * pieces of noise, and the trail is unreadable with them in it. Where the
 * repeat means something the count stays on the line, so nothing is
 * silently dropped.
 */
/*
 * Things that can only happen once, however many places recorded them.
 *
 * Promoting a lead writes three rows between them — the promote route's own
 * line, the note from the window that did it, and the row's stage reaching
 * the last rung — and collapse() only folds neighbours, so on a trail with a
 * call logged in between they stayed three. The journey read "Moved to a
 * deal" twice with somebody else's work sandwiched between the two.
 *
 * A record is promoted once, moved onto the ledger once, and turned away
 * once. Keep the last of each, because that is when it actually settled, and
 * carry forward the note and the name from the earlier copies — the reason
 * somebody typed is usually on the first one and the final date is on the
 * last, and neither half is worth losing.
 */
const ONCE = new Set(['converted', 'client', 'dropped', 'lost']);

function once(list) {
  const last = new Map();
  list.forEach((e, i) => { if (ONCE.has(e.kind)) last.set(e.kind, i); });
  if (!last.size) return list;

  const carried = new Map();
  list.forEach(e => {
    if (!ONCE.has(e.kind)) return;
    const got = carried.get(e.kind) || {};
    /* First note wins, first name wins: the earliest copy is the one
       somebody actually typed into. */
    if (!got.note && e.note) got.note = e.note;
    if (!got.who && e.who) got.who = e.who;
    carried.set(e.kind, got);
  });

  return list.filter((e, i) => {
    if (!ONCE.has(e.kind)) return true;
    if (last.get(e.kind) !== i) return false;
    const got = carried.get(e.kind) || {};
    if (!e.note && got.note) e.note = got.note;
    if (!e.who && got.who) e.who = got.who;
    return true;
  });
}

/*
 * One thing somebody did, on one line.
 *
 * The log form moves the stage as well as recording the call — that is the
 * point of it, and it is one action by one person — but it writes a row for
 * each: the call, "Moved to Demo", and, where the stage was the last one,
 * "Converted to a deal" from the promote route as well. Three lines came out
 * of one form, and the trail read as though somebody had rung, then gone away
 * and moved the card, then come back and promoted it.
 *
 * So a stage or a promotion written in the same breath as a piece of work
 * rides on that work's line instead of taking one of its own. Nothing is
 * deleted — every row is still there, still auditable, still exactly what
 * each subsystem wrote — this only decides how they are read.
 *
 * "The same breath" is the clock, to the second, and it has to be: a call
 * logged in the morning and a card moved in the afternoon are two things
 * somebody did, and `due` is a date that cannot tell them apart. One real
 * trail here reads 00:36:08 for a call and its stage move, then 00:43:16-17
 * for a promotion and the call that caused it — two submissions, seven
 * minutes apart, six rows between them.
 *
 * Not matched on who. The name is right on both halves now, but until today
 * every stage move was signed with the workspace's mail signature rather
 * than the person who made it, so on trails already written one submission
 * carries two different names. The clock does not lie about that.
 */
const RIDES = new Set(['stage', 'converted']);

/* Wide enough for a form that writes three rows with a round trip between
   them, far short of anything a person does as a second action. */
const SAME_BREATH = 120 * 1000;

function fold(list) {
  /* The whole trail in hand before anything is matched, because the promote
     route's row lands between the call and the stage note as often as after
     them — a search that only looks backwards picks the wrong host half the
     time. */
  const work = list.filter(e => e.kind === 'work' && e.wroteAt);
  if (!work.length) return list;

  return list.filter(e => {
    /* Nothing to key on: a row written before the column existed, or one
       still only in the browser. It keeps its own line, which is the answer
       that is merely verbose rather than wrong. */
    if (!RIDES.has(e.kind) || !e.wroteAt) return true;

    let host = null;
    let gap = SAME_BREATH;
    work.forEach(x => {
      const d = Math.abs(x.wroteAt - e.wroteAt);
      if (d <= gap) { gap = d; host = x; }
    });
    if (!host) return true;

    if (!host.moved) host.moved = [];
    if (!host.moved.includes(e.title)) host.moved.push(e.title);
    return false;
  });
}

function collapse(list) {
  const out = [];
  list.forEach(e => {
    const prev = out[out.length - 1];
    if (prev && prev.kind === e.kind && prev.title === e.title
      && prev.note === e.note && day(prev.at) === day(e.at)) {
      if (COUNTABLE.has(prev.kind)) prev.times = (prev.times || 1) + 1;
      return;
    }
    out.push(e);
  });
  return out;
}

/**
 * The piece of work still open on this deal, if there is one.
 *
 * One round at a time: a second "Call them" logged while the first is
 * still unticked is how a board ends up with a hundred open tasks and no
 * record of which was actually done — this database has exactly that, 110
 * of them. The trail offers to log the next thing only once the last one
 * has been closed off.
 */
export const openWork = (crm, deal) =>
  (deal ? dealActivities(crm, deal.id).find(a => !a.done) : null) || null;

/**
 * Winning is recorded three times and is one event.
 *
 * Moving a row to Won writes "Moved to Won" here, the promote endpoint
 * writes "Won — converted to a deal" on the server, and the row's own
 * stage makes a third. The trail said all three, one under the other,
 * with the same date. They are folded onto one title so collapse() can
 * see they are the same thing.
 */
/*
 * Reaching the last stage.
 *
 * There is no "Won" on this trail any more. A lead is moved to a deal and a
 * deal is moved to a client, and both of those were being written as wins —
 * so a board of leads nobody had closed read as a month of sales, three
 * times over on the same day: a stage note, a promote note and the row's own
 * stage all saying "Won" one under the other.
 *
 * The stage note is dropped entirely rather than relabelled. It is the same
 * event as the promotion beside it, and two lines for one thing is what made
 * the trail unreadable in the first place.
 */
const isWinNote = a => /^(Moved to Won|Won)$/i.test(String(a.subject || '').trim());

/* Promoted off the pipeline. Its own kind, because it is not a win — see
   KIND.converted. The two older wordings are still in the log on installs
   that have not run the backfill. */
const isPromoteNote = a => /^(Converted to a deal|Won — converted to a deal|Won - converted to a deal)$/i
  .test(String(a.subject || '').trim());

/* And onto the ledger. Written by the convert route; the older wording is
   still in the log on installs that have not run the backfill. */
const isClientNote = a => /^(Moved to a client|Converted to client)$/i
  .test(String(a.subject || '').trim());

/* Turned away, written by the drop route. */
const isDropNote = a => /^Not interested( —|-|$)/i.test(String(a.subject || '').trim());

/**
 * Notes the app wrote about the board, as opposed to somebody's own work.
 *
 * Exported because the pipeline row shows a log of what was actually done,
 * and "Moved to Demo" is not something anybody did — it is the board saying
 * where the row sits, written by moveDealStage. It reads as work on the one
 * list and as a stage on the other, and the two screens have to agree about
 * which sentences are which.
 */
export const isStageNote = a => /^Moved to /i.test(String(a.subject || ''));

/*
 * The note the move-to-deal window writes.
 *
 * It reads "Moved to Deals" and carries whatever somebody typed in the box,
 * and it is written a second after the promote route's own line — so one
 * decision landed on the trail twice, once with the reason and once without.
 * It is the same event; the note it carries is the half worth keeping.
 */
const isMoveNote = a => /^Moved to Deals$/i.test(String(a.subject || '').trim());
/* Somebody putting their name to the lead. It is the first thing that
   happens to one and it deserves its own mark, not "Note — Taken on by". */
const isTakeNote = a => /^Taken on by /i.test(String(a.subject || ''));
const isSystemNote = a => /^(Converted to client|Brought back from Clients|Added to the pipeline)/i
  .test(String(a.subject || ''));

/**
 * The demo booking behind a deal.
 *
 * The link is only written when somebody presses "Add to the pipeline" on
 * a booking — 2 of 337 sessions carry one. Everything else falls back to
 * the address, which is the only thing a booking and a lead share, and
 * which finds another 90.
 */
export function bookingOfDeal(demos, deal) {
  if (!demos || !deal) return null;
  const list = demos.bookings || [];
  const byDeal = list.find(b => b.session && b.session.dealId === deal.id);
  if (byDeal) return byDeal;

  const email = norm(deal.leadEmail || deal.lead_email);
  if (!email) return null;
  return list.find(b => norm(b.invitee?.email) === email) || null;
}

/** And the same join from the other side. */
export function dealOfBooking(crm, booking) {
  if (!crm || !booking) return null;
  const id = booking.session?.dealId;
  if (id) {
    const hit = (crm.deals || []).find(d => d.id === id);
    if (hit) return hit;
  }
  const email = norm(booking.invitee?.email);
  if (!email) return null;
  return (crm.deals || []).find(d => norm(d.leadEmail || d.lead_email) === email) || null;
}

/* ------------------------------------------------------------- the parts */

/** The lead arriving, and everything logged against it since. */
function dealEvents(crm, deal, push, due) {
  push(deal.created, 'arrived',
    deal.source ? `Arrived from ${deal.source}` : 'Arrived on the pipeline',
    deal.origin === 'xyra' ? 'Registered in the app' : '',
    '');

  dealActivities(crm, deal.id).forEach(a => {
    if (!a.done) {
      due.push({
        at: parseDate(a.due), type: a.type || 'Task',
        title: a.subject || 'Something to do', who: a.who || '', act: a
      });
      return;
    }
    /* Dropped: the promotion beside it is the same event, said once. */
    if (isWinNote(a)) return;
    if (isPromoteNote(a) || isMoveNote(a)) {
      push(a.due, 'converted', 'Moved to a deal', a.notes, a.who, null, a.board, a.at);
      return;
    }
    if (isClientNote(a)) { push(a.due, 'client', 'Moved to a client', a.notes, a.who, null, a.board, a.at); return; }
    if (isDropNote(a)) { push(a.due, 'dropped', a.subject, a.notes, a.who, null, a.board, a.at); return; }

    const kind = isStageNote(a) ? 'stage'
      : isTakeNote(a) ? 'took'
        : isSystemNote(a) ? 'client' : 'work';
    /* The type is the headline for real work — "Call" says more at a glance
       than the subject does — and noise on a note the app wrote itself. */
    push(a.due, kind, kind === 'work' ? `${a.type || 'Note'} — ${a.subject}` : a.subject,
      a.notes, a.who, kind === 'work' ? a : null, a.board, a.at);
  });
}

/** The demo: booked, held, handed across, and chased. */
function bookingEvents(booking, push) {
  const s = booking.session || {};

  push(booking.bookedAt, 'booked',
    `Booked a demo${booking.source?.source ? ` · ${booking.source.source}` : ''}`,
    [booking.eventTypeName || booking.name, booking.duration ? `${booking.duration} min` : '']
      .filter(Boolean).join(' · '),
    booking.invitee?.name || '');

  if (booking.cancellation) {
    push(booking.cancellation.at || booking.start, 'lost',
      `Cancelled by the ${booking.cancellation.by}`, booking.cancellation.reason, '');
  } else {
    push(booking.start, 'demo', 'Demo held',
      [s.attendance, s.outcomeLabel || s.outcome, s.demoNotes].filter(Boolean).join(' · '),
      booking.host?.name || '');
  }

  /*
   * The hand-over comes before the rounds by definition — a round is
   * somebody chasing what they were handed. A session that was reopened
   * and handed again keeps only the latest hand-over time, which sorts
   * below the rounds it caused, so it is anchored to whichever came
   * first rather than left to float down the trail.
   */
  const rounds = (s.feedback || []).filter((f, i) => !(i === 0 && !f.kind));
  if (s.handedAt) {
    const firstRound = rounds.map(f => parseDate(f.at)).filter(Boolean)
      .sort((x, y) => x - y)[0];
    const handed = parseDate(s.handedAt);
    push(firstRound && handed && firstRound < handed ? firstRound : handed,
      'handover', 'Handed to sales', s.handedNote, s.handedBy);
  }

  /* Numbered as they were logged, not by their place in the array: the
     hand-over note sits in the same list and skipping it by index left a
     trail that started at Round 0. */
  rounds.forEach((f, i) => push(f.at, 'round', `Round ${i + 1}`, f.text, f.by));

  if (s.followStatus === 'closed') {
    push((s.feedback || []).slice(-1)[0]?.at || s.handedAt, 'round',
      `Follow-up closed${s.closedAsLabel ? ` — ${s.closedAsLabel}` : ''}`, s.closedReason, '');
  }
}

/* ------------------------------------------------------------- the whole */

/**
 * The trail for one person, from whichever end is in hand.
 *
 * Pass a `deal`, a `booking`, or both. Returns `{ events, due, deal,
 * booking }` — what happened, what is still owed, and the two records it
 * was built from, so a caller can link through to the one it did not
 * already have.
 */
export function journeyOf({ crm, rows, demos, deal, booking, S }) {
  const b = booking || bookingOfDeal(demos, deal);
  const d = deal || dealOfBooking(crm, b);
  if (!d && !b) return { events: [], due: [], deal: null, booking: null };

  const out = [];
  const due = [];
  /* `act` is the activity behind the line, where there is one. The trail is
     where somebody reads what was logged, so it is also where they will want
     to correct it or tick it off — the rest of the events are records of
     things that happened and have nothing to edit. */
  const push = (at, kind, title, note, who, act, board, wroteAt) => {
    const when = parseDate(at);
    if (!when) return;
    out.push({
      at: when, kind, title, note: note || '', who: who || '', act: act || null,
      /* To the second, where the row carries it — see `at` in toActivity.
         It is what says whether two rows came out of one form. */
      wroteAt: wroteAt ? parseDate(wroteAt) : null,
      /* Which board this line was written on, where anything knows. Empty on
         everything that is not an activity — the arrival, the demo, the
         booking — because those happen to the record rather than on a board. */
      board: board || ''
    });
  };

  if (d) dealEvents(crm, d, push, due);
  const first = out[0] || null;
  if (b) bookingEvents(b, push);

  /* ---- how the deal itself ended */
  /*
   * The row's own stage, said in the words of what actually happened.
   *
   * 'Won' is a stage name in the settings, not something the trail should
   * repeat: a lead that reached it was moved to a deal, and a deal that
   * reached it was moved to a client. Which of the two is decided by whether
   * an account exists, so the same stage reads correctly on both boards —
   * and it is folded onto the same title as the note above it, so one event
   * lands on one line rather than two.
   */
  if (d?.stage === 'Won') {
    const onLedger = !!(d.clientId || clientOfDeal(rows, d));
    push(d.close, onLedger ? 'client' : 'converted',
      onLedger ? 'Moved to a client' : 'Moved to a deal', '', d.owner);
  }
  if (d?.stage === 'Lost') push(d.close, 'lost', 'Lost', d.lostReason, d.owner);
  /* And the end of the road, where there is one. Drawn off the row rather
     than only off its activity, so a drop recorded offline still closes the
     trail rather than leaving it hanging. */
  if (d?.dropped) {
    push(d.dropped, 'dropped', `Not interested — ${d.dropReason || 'no reason given'}`,
      d.dropNote || '', d.owner);
  }

  /* ---- and whether they started paying */
  const client = (d && clientOfDeal(rows, d))
    || (b?.session?.clientId && (rows || []).find(r => String(r.id) === String(b.session.clientId)))
    || null;
  if (client) {
    push(client.reg_on || client.regOn || d?.close || b?.start, 'client',
      `On the books as ${client.name}`,
      [client.plan, client.cycle].filter(Boolean).join(' · '), '');
  }

  /*
   * The arrival is the row's created_at, which is when the sync wrote it,
   * not when the lead turned up: the tracker back-dates its "Demo
   * requested" activity to the day it saw them, so the arrival kept
   * landing below the thing that caused it. Pull it back to the earliest
   * moment anything is known, which is what it means.
   */
  if (first && first.kind === 'arrived') {
    const earliest = out.reduce((t, e) => (e.at < t ? e.at : t), first.at);
    if (earliest < first.at) first.at = earliest;
  }

  /* Oldest first: a journey is read forwards, unlike a list of activities,
     which is read newest-first because the newest is the one being acted on. */
  out.sort((a, x) => a.at - x.at);
  due.sort((a, x) => (a.at || Infinity) - (x.at || Infinity));

  void S;
  /* Neighbours folded first, then the things that can only have happened
     once — in that order, because folding a repeat can put two copies of a
     once-only event next to each other and the second pass is what removes
     them for good. */
  /* Neighbours folded, then the things that can only have happened once,
     then what one person did in one action onto one line. In that order:
     the last pass looks for work to ride on, and it must not find a copy
     the first two were about to remove. */
  return { events: fold(once(collapse(out))), due, deal: d || null, booking: b || null };
}

/** The deal-first entry point, which is how the pipeline reaches it. */
export const dealJourney = args => journeyOf(args);
